/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, signal, Component, OnInit} from '@angular/core';

import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import template from './political-sponsors-page.component.html';
import {SignalPageState} from '../../common/controller/signal-page-state';
import {PoliticalEntityApiService} from '../../political-entity/political-entity-api.service';
import {PoliticalSponsorApiService} from '../../political-entity/political-sponsor-api.service';

const GROUP_BY_OPTIONS = [
  {value: 'SPONSOR', label: 'Sponsor'},
  {value: 'CONTROLLING', label: 'Controlling entity'},
  {value: 'PAYING', label: 'Payer'}
];

@Component({
  standalone: false,
  selector: 'adn-political-sponsors-page',
  template
})
export class PoliticalSponsorsPageComponent implements OnInit {
  readonly groupByOptions = GROUP_BY_OPTIONS;

  groupBy = 'SPONSOR';
  readonly generating = signal<Record<string, boolean>>({});
  private readonly destroyRef: DestroyRef;
  readonly page: SignalPageState<any, {groupBy: string}>;
  get results(): any[] { return this.page.items(); }
  get searched(): boolean { return this.page.loaded(); }
  constructor(
    private readonly politicalSponsorApi: PoliticalSponsorApiService,
    private readonly politicalEntityApi: PoliticalEntityApiService,
    destroyRef: DestroyRef
  ) {
    this.destroyRef = destroyRef;
    this.page = new SignalPageState((query, page, pageSize) => this.politicalSponsorApi.summary({...query, page, pageSize}), 50, destroyRef);
  }

  ngOnInit(): void {
    this.runQuery(true);
  }

  groupByLabel(): string {
    const option = this.groupByOptions.find((candidate) => candidate.value === this.groupBy);
    return option ? option.label : this.groupBy;
  }

  declaredEntity(advertiser: any, type: 'CONTROLLING' | 'PAYING'): any {
    return (advertiser.entities || []).find((entity: any) => entity.type === type) || null;
  }

  generatePoliticalEntities(group: any): void {
    const advertiser = group.advertisers && group.advertisers[0];
    if (!advertiser || !advertiser.id || this.generating()[advertiser.id]) {
      return;
    }
    group.generationFailed = false;
    this.generating.update((items) => ({...items, [advertiser.id]: true}));
    this.politicalEntityApi.fromAdvertiser(advertiser.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.generating.update((items) => ({...items, [advertiser.id]: false}));
        group.generatedEntityCount = (data.results || []).length;
        group.generated = true;
      },
      error: () => {
        this.generating.update((items) => ({...items, [advertiser.id]: false}));
        group.generationFailed = true;
      }
    });
  }

  runQuery(resetPage = false): void {
    this.page.load({groupBy: this.groupBy}, resetPage);
  }
}
